/**
 * <s-product-gallery> — main product image track with a thumbnail strip.
 *
 * Markup contract (inside the element):
 *   [data-gallery-track]   horizontal snap track, one slide per media item
 *   [data-gallery-thumbs]  strip of buttons with commandfor=<this id>, command="--show" and data-index
 * Thumbnails scroll the track to their slide; swiping the track updates the active thumbnail.
 */
class SProductGallery extends HTMLElement {
  #ac = null;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('command', (event) => {
      if (event.command !== '--show') return;
      const source = event.source;
      if (!(source instanceof HTMLElement)) return;
      this.#show(Number.parseInt(source.dataset.index || '0', 10));
    }, { signal });

    document.addEventListener('product:variant-media', (event) => {
      this.#showMedia(event.detail?.mediaId);
    }, { signal });
    requestAnimationFrame(() => {
      if (this.isConnected) this.#showMedia(this.dataset.selectedMediaId);
    });

    const track = this.#track;
    track?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#syncThumbs());
    }, { signal, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #thumbs() {
    return [...this.querySelectorAll('[data-gallery-thumbs] [data-index]')];
  }

  #showMedia(mediaId) {
    if (!mediaId || !this.#track) return;
    const index = [...this.#track.children].findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.#show(index);
  }

  #show(index) {
    const track = this.#track;
    const slide = track?.children[index];
    if (!(slide instanceof HTMLElement)) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll the track only, so the page itself doesn't jump.
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #syncThumbs() {
    const track = this.#track;
    if (!track || track.clientWidth === 0) return;
    // Logical scroll position, so right-to-left stores pick the right slide too.
    const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
    this.#setActive(index);
  }

  #setActive(index) {
    for (const thumb of this.#thumbs) {
      const active = Number.parseInt(thumb.dataset.index || '-1', 10) === index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        const strip = thumb.parentElement;
        if (strip) {
          const target = thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
          strip.scrollTo({ left: target, behavior: 'smooth' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
